import { useState } from 'react'
import { formatCents, parseMoneyInput } from '../../api/parseMoney'
import {
  KIND_LABELS,
  isValidThresholdCents,
  type NotificationPreferences as Prefs,
  type SummaryFrequency,
} from '../../api/notificationPreferences'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { Field } from '../../platform/primitives/Field'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { Switch } from '../../platform/primitives/Switch'
import {
  useNotificationPreferencesState,
  useSetLargeTransactionThreshold,
  useSetNotificationKind,
  useSetSummaryFrequency,
} from './useNotificationPreferences'
import './preferences.css'

/**
 * Settings › Preferences › Notifications — M13. One switch per switchable type;
 * the rest are always sent. Low balance has no backend field, so it is not here.
 *
 * The threshold label carries NO currency symbol: the backend compares it against each
 * transaction's own amount in that transaction's currency, unconverted, and uses abs()
 * so refunds count. THRESHOLD_HELP says so; it sits on the --surface row card.
 */
const THRESHOLD_LABEL = 'Alert me about transactions over'
export const THRESHOLD_HELP = 'Applies to each transaction in its own currency, refunds included.'
const FREQUENCY_OPTIONS: { value: SummaryFrequency; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
]

export function NotificationPreferences() {
  const state = useNotificationPreferencesState()
  return (
    <>
      <h2 className="preferences__title" id="notifications-title">
        Notifications
      </h2>
      {/* Page background, not a card: --ink, since --ink2 misses 4.5:1 here in dark. */}
      <p className="preferences__note">Alerts are delivered to the TrackE5 iPhone app. The web app can’t send push notifications yet.</p>
      <Async state={state} surface="notification-preferences" skeleton={<Skeleton />} empty={{ title: 'No notification settings' }}>
        {(prefs) => <Rows prefs={prefs} />}
      </Async>
    </>
  )
}

function Rows({ prefs }: { prefs: Prefs }) {
  const setKind = useSetNotificationKind()
  const setFrequency = useSetSummaryFrequency()
  const summaryOn = prefs.types.find((t) => t.kind === 'spending_summary')?.enabled === true

  return (
    <>
      {prefs.types.map(({ kind, enabled }) => (
        <div className="preferences__row preferences__row--toggle" key={kind}>
          <div className="preferences__text">
            <span className="preferences__name">{KIND_LABELS[kind].label}</span>
            <p className="preferences__help" id={`notif-help-${kind}`}>
              {KIND_LABELS[kind].help}
            </p>
          </div>
          <span className="preferences__toggle">
            <Switch
              label={KIND_LABELS[kind].label}
              describedBy={`notif-help-${kind}`}
              checked={enabled}
              onChange={(next) => setKind.mutate({ kind, enabled: next })}
            />
          </span>
          {kind === 'large_transaction' && <Threshold cents={prefs.largeTransactionThresholdCents} />}
          {kind === 'spending_summary' && summaryOn && (
            <div className="preferences__extra">
              <SegmentedControl
                label="Spending summary frequency"
                name="spending-summary-frequency"
                options={FREQUENCY_OPTIONS}
                value={prefs.spendingSummaryFrequency}
                onChange={(next) => setFrequency.mutate(next)}
              />
            </div>
          )}
        </div>
      ))}
    </>
  )
}

/**
 * One mounted field: remounting on a saved value would drop keyboard focus after Enter and wipe half-typed text on
 * a refetch. The text re-derives from the server only when the user is not focused in it or has not changed it.
 */
function Threshold({ cents }: { cents: number }) {
  const save = useSetLargeTransactionThreshold()
  const [text, setText] = useState(formatCents(cents))
  const [error, setError] = useState<string | undefined>()
  // The last value sent, so Enter followed by the blur it causes sends once.
  const [sent, setSent] = useState(cents)
  const [shown, setShown] = useState(cents)
  const [focused, setFocused] = useState(false)
  if (cents !== shown) {
    setShown(cents)
    setSent(cents)
    // The server echoing what we just sent is our own save landing: normalise the text. Anything else (a refetch
    // bringing another device's value) must not overwrite a draft the user is typing.
    if (!focused || text === formatCents(shown) || cents === sent) {
      setText(formatCents(cents))
      setError(undefined)
    }
  }

  const commit = () => {
    let next: number
    try {
      next = parseMoneyInput(text)
    } catch {
      return setError('Enter an amount like 500 or 1,250.50.')
    }
    if (!isValidThresholdCents(next)) return setError('Enter an amount like 500 or 1,250.50, up to 1,000,000.')
    setError(undefined)
    if (next === sent) return
    setSent(next)
    save.mutate(next, {
      onError: () => {
        setSent(cents)
        setError('We couldn’t save that. Try again.')
      },
    })
  }

  return (
    <form
      className="preferences__extra"
      onSubmit={(e) => {
        e.preventDefault()
        commit()
      }}
    >
      <Field
        label={THRESHOLD_LABEL}
        hint={THRESHOLD_HELP}
        inputMode="decimal"
        value={text}
        error={error}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false)
          commit()
        }}
      />
    </form>
  )
}
